import {Box, FormControl, MenuItem, Select, TextField, Typography, Tooltip} from '@mui/material';
import {useEffect, useState} from 'react';
// import {roomServiceCreate, roomServiceUpdate} from 'src/apis';
// import {Room} from 'src/apis/autoGenerate';
import {roomServiceCreate, roomServiceUpdate} from 'src/apis/autoGenerate/services';
import {Room} from 'src/apis/autoGenerate/types/room';
import message from 'src/components/Message';
import Modal from 'src/components/Modal';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import InfoIcon from '@mui/icons-material/Info';

interface AddUnitModalProps {
  open: boolean;
  handleConfirm: VoidFunction;
  handleCancel: VoidFunction;
  buildingId: string;
  initData?: Room;
}

enum UnitStatus {
  unknown = 'unknown',
  vacant = 'vacant',
  occupied = 'occupied',
}

function getInitFormData() {
  return {
    floorName: '',
    name: '',
    status: UnitStatus.unknown,
    pinCode: '',
  };
}

export default function AddUnitModal({
  open,
  initData,
  handleConfirm,
  handleCancel,
  buildingId,
}: AddUnitModalProps) {
  const [loading, setLoading] = useState(false);
  const account = useAppSelector(selectAccount);

  const [formData, setFormData] = useState(
    initData
      ? {
          floorName: initData.floorName,
          name: initData.name,
          status: initData.status || UnitStatus.unknown,
          pinCode: initData.pinCode || '',
        }
      : getInitFormData()
  );

  useEffect(() => {
    if (initData) {
      setFormData({
        floorName: initData.floorName,
        name: initData.name,
        status: initData.status || UnitStatus.unknown,
        pinCode: initData.pinCode || '',
      });
    }
  }, [initData]);

  const handleAddUnit = () => {
    setLoading(true);
    if (account?.userDetail?.organizations && account?.userDetail?.organizations[0].id) {
      let callFunc = !initData
        ? roomServiceCreate({
            room: {
              ...formData,
              buildingId,
            },
          })
        : roomServiceUpdate(initData.id as string, {
            room: {
              ...formData,
              buildingId,
            },
          });

      callFunc
        .then(res => {
          handleConfirm();
        })
        .catch(err => {
          message.error(err.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <Modal
      loading={loading}
      open={open}
      title={initData ? 'Edit Unit' : 'Add Unit'}
      handleCancel={handleCancel}
      handleConfirm={handleAddUnit}
      modalContent={
        <Box component="form">
          {/* Unit Name */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Unit name
          </Typography>
          <TextField
            value={formData.name}
            fullWidth
            size="small"
            id="name"
            placeholder="Unit name"
            variant="outlined"
            onChange={(e: any) => {
              setFormData({...formData, name: e.target.value});
            }}
            sx={{mb: 3}}
          />

          {/* Floor Number */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Floor No
          </Typography>
          <TextField
            value={formData.floorName}
            fullWidth
            size="small"
            id="floorName"
            placeholder="Floor No"
            variant="outlined"
            onChange={(e: any) => {
              setFormData({...formData, floorName: e.target.value});
            }}
            sx={{mb: 3}}
          />

          {/* Occupied Status */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Occupied status
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              id="status-select"
              value={formData.status}
              variant="outlined"
              onChange={(e: any) => {
                setFormData({...formData, status: e.target.value});
              }}
              displayEmpty
              sx={{mb: 3}}
            >
              <MenuItem value={UnitStatus.unknown}>{UnitStatus.unknown}</MenuItem>
              <MenuItem value={UnitStatus.occupied}>{UnitStatus.occupied}</MenuItem>
              <MenuItem value={UnitStatus.vacant}>{UnitStatus.vacant}</MenuItem>
            </Select>
          </FormControl>

          {/* Pin Code (read-only if present) */}
          {formData.pinCode && (
            <>
              <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                <Typography variant="h6" sx={{fontWeight: 400, fontSize: '1.1rem', mb: 0}}>
                  Pin Code
                </Typography>
                <Tooltip
                  title="PinCode can be modified by Resident from Resident App"
                  placement="right"
                  arrow
                  sx={{
                    tooltip: {
                      backgroundColor: '#f5f5f9',
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontSize: '13px',
                      border: '1px solid #dadde9',
                      maxWidth: 300,
                      padding: '8px 12px',
                    },
                    arrow: {
                      color: '#f5f5f9',
                      '&:before': {
                        border: '1px solid #dadde9',
                      },
                    },
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      color: '#888',
                      cursor: 'pointer',
                      fontSize: 18,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <InfoIcon fontSize="small" color="action" />
                  </Box>
                </Tooltip>
              </Box>
              <TextField
                value={formData.pinCode}
                fullWidth
                size="small"
                id="pinCode"
                placeholder="Pin Code"
                variant="outlined"
                InputProps={{readOnly: true, disabled: true}}
                sx={{mb: 3}}
              />
            </>
          )}
        </Box>
      }
    />
  );
}
